import { useEffect, useState } from 'react'

import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import { alpha } from '@mui/material/styles'

import { observer } from 'mobx-react-lite'

import { Text } from '@mxenabled/mxui'

import { Dialog, useGlobalCopyStore } from '../../common'

export type DismissType = 'once' | 'always'

export interface DismissInsightDialogProps {
  isOpen: boolean
  onClose: () => void
  onDismiss: (type: DismissType) => void
}

/** Confirmation dialog for hiding an insight. */
export const DismissInsightDialog = observer(
  ({ isOpen, onClose, onDismiss }: DismissInsightDialogProps) => {
    const copy = useGlobalCopyStore().insights_feed?.dismiss
    const [selected, setSelected] = useState<DismissType>('once')

    useEffect(() => {
      if (isOpen) setSelected('once')
    }, [isOpen])

    if (!copy) return null

    return (
      <Dialog
        copy={{ title: copy.title, close_aria: copy.close_aria }}
        isOpen={isOpen}
        onClose={onClose}
        onPrimaryAction={() => onDismiss(selected)}
        primaryText={copy.confirm}
        secondaryText={copy.cancel}
        sx={{
          '& .MuiDialogContent-root': { py: 0 },
          '& .MuiDialog-paperScrollPaper > div:first-child': { pt: 24, pb: 8 },
          '& .MuiDialog-paperScrollPaper > div:first-child .MuiIconButton-root': { my: -10 },
          '& .MuiDialog-paperScrollPaper': {
            bgcolor: 'background.paper',
            borderRadius: '4px',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
          },
          '& .MuiDialog-paper:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
          },
          '& .MuiButton-outlined': {
            border: 'none',
            '&:hover': { bgcolor: 'transparent', border: 'none' },
          },
        }}
      >
        <Stack sx={{ gap: 24 }}>
          <Text variant="subtitle1">{copy.description}</Text>

          <RadioGroup
            aria-label={copy.title}
            name="dismiss-type"
            onChange={(e) => setSelected(e.target.value as DismissType)}
            sx={{ m: 0, p: 0 }}
            value={selected}
          >
            <Stack sx={{ gap: 16 }}>
              {(['once', 'always'] as const).map((k) => (
                <FormControlLabel
                  key={k}
                  value={k}
                  control={
                    <Radio
                      inputProps={{ 'aria-label': copy[k].headline }}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          color: selected === k ? 'inherit' : 'action.active',
                        },
                      }}
                    />
                  }
                  labelPlacement="start"
                  label={
                    <Stack sx={{ gap: 4, flexGrow: 1 }}>
                      <Text bold={true} variant="body1">
                        {copy[k].headline}
                      </Text>
                      <Text variant="subtitle2">{copy[k].description}</Text>
                    </Stack>
                  }
                  sx={{
                    alignItems: 'center',
                    borderRadius: '8px',
                    justifyContent: 'space-between',
                    m: 0,
                    minHeight: 72,
                    px: 16,
                    py: 0,
                    border: (theme) =>
                      selected === k
                        ? `2px solid ${alpha(theme.palette.primary.main, 0.5)}`
                        : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                />
              ))}
            </Stack>
          </RadioGroup>
        </Stack>
      </Dialog>
    )
  },
)

export default DismissInsightDialog
